import { useEffect, useRef } from "react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

/**
 * Global runtime translator — translates EVERY visible text node on the page
 * into the active language (not only <Tx> strings), covering all pages,
 * dynamic content, menus, product/academy/ruqyah descriptions, etc.
 * Reuses POST /api/i18n/translate (MongoDB-cached + LLM) and localStorage.
 */
const LS_PREFIX = "icc_ui_tr_v1_";
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "INPUT", "SELECT", "OPTION"]);
const HAS_LATIN = /[A-Za-z]/;

const nodeMeta = new WeakMap(); // textNode -> { orig, lang }

const loadLocal = (lang) => { try { return JSON.parse(localStorage.getItem(LS_PREFIX + lang) || "{}"); } catch { return {}; } };
const saveLocal = (lang, map) => { try { localStorage.setItem(LS_PREFIX + lang, JSON.stringify(map)); } catch { /* quota */ } };

const shouldSkip = (parent) => {
  for (let el = parent; el && el !== document.body; el = el.parentElement) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.getAttribute && (el.getAttribute("data-no-translate") !== null || el.getAttribute("translate") === "no")) return true;
    if (el.classList && el.classList.contains("arabic")) return true; // Qur'anic/Arabic script stays
  }
  return false;
};

const collect = (lang) => {
  const nodes = [];
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const v = n.nodeValue;
      if (!v || !HAS_LATIN.test(v)) return NodeFilter.FILTER_REJECT;
      if (!n.parentElement || shouldSkip(n.parentElement)) return NodeFilter.FILTER_REJECT;
      const m = nodeMeta.get(n);
      if (m && m.lang === lang) return NodeFilter.FILTER_REJECT; // already done for this lang
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node;
  while ((node = walker.nextNode())) nodes.push(node);
  return nodes;
};

const chunk = (arr, size) => { const o = []; for (let i = 0; i < arr.length; i += size) o.push(arr.slice(i, i + size)); return o; };

const AutoTranslateDOM = () => {
  const { lang } = useLanguage();
  const applyingRef = useRef(false);
  const timerRef = useRef(null);
  const langRef = useRef(lang);

  useEffect(() => {
    langRef.current = lang;

    const restoreToEnglish = () => {
      applyingRef.current = true;
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null);
      let n;
      while ((n = walker.nextNode())) {
        const m = nodeMeta.get(n);
        if (m && m.orig != null && n.nodeValue !== m.orig) { n.nodeValue = m.orig; m.lang = "en"; }
      }
      applyingRef.current = false;
    };

    const run = async () => {
      const L = langRef.current;
      if (!L || L === "en") { restoreToEnglish(); return; }
      const nodes = collect(L);
      if (!nodes.length) return;
      const local = loadLocal(L);
      // Record originals; use trimmed text as translation key
      const items = nodes.map((n) => {
        const m = nodeMeta.get(n) || { orig: n.nodeValue };
        nodeMeta.set(n, { ...m, orig: m.orig ?? n.nodeValue });
        const raw = nodeMeta.get(n).orig;
        return { n, raw, key: raw.trim(), lead: raw.match(/^\s*/)[0], tail: raw.match(/\s*$/)[0] };
      });
      const apply = () => {
        applyingRef.current = true;
        for (const it of items) {
          const tr = local[it.key];
          if (tr) { it.n.nodeValue = it.lead + tr + it.tail; }
          nodeMeta.set(it.n, { orig: it.raw, lang: L });
        }
        applyingRef.current = false;
      };
      const missing = [...new Set(items.map((i) => i.key).filter((k) => k && !local[k]))];
      apply(); // apply whatever is already cached immediately
      if (!missing.length) return;
      try {
        await Promise.all(
          chunk(missing, 40).map(async (texts) => {
            const r = await api.post("/i18n/translate", { texts, lang: L });
            Object.assign(local, r?.data?.translations || {});
          })
        );
        saveLocal(L, local);
        apply();
      } catch { /* leave English on failure */ }
    };

    run();
    const schedule = () => { if (timerRef.current) clearTimeout(timerRef.current); timerRef.current = setTimeout(run, 400); };
    const observer = new MutationObserver(() => { if (!applyingRef.current) schedule(); });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => { observer.disconnect(); if (timerRef.current) clearTimeout(timerRef.current); };
  }, [lang]);

  return null;
};

export default AutoTranslateDOM;
